import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import moment from "moment";

import useAPI from "services/Api";
import Page from "pages/Page";
import SessionBadgeRow from "sessions/SessionBadgeRow";
import SessionBonusPanel from "sessions/SessionBonusPanel";
import SessionDivider from "sessions/SessionDivider";
import SessionHeader from "sessions/SessionHeader";
import SessionRoutinePanel from "sessions/SessionRoutinePanel";
import SessionWeekStart from "sessions/SessionWeekStart";
import { navigate } from "utility/NavigationService";
import { renderLoader } from "utility/LoaderUtils";
import { useNotificationManager } from "notifications/NotificationManager";

const Sessions = ({ componentName = "[Sessions]" }) => {
    const [headerInfo, setHeaderInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [sessionData, setSessionData] = useState(null);
    const [sessionEvents, setSessionEvents] = useState(null);
    const [startWeek, setStartWeek] = useState(0);

    const API = useAPI();
    const notificationManager = useNotificationManager();
    const patientId = Number(useParams().id);
    const sessionDate = moment(useParams().date, "YYYY-MM-DD");

    // ---=== Utilities ===--- //

    const capitalizeWords = (sentence) => {
        let words = sentence.split(" ");
        words.forEach((word, i) => {
            words[i] = word.charAt(0).toUpperCase() + word.slice(1);
        });
        return words.join(" ");
    };

    const goToPatientActivity = () => {
        void navigate(`/patient/${patientId}/usage`);
    };

    // ---=== KarunaCLOUD ===--- //

    const getSessionData = async(id) => {
        await API.api_Get(
            componentName,
            `/sessions/?patient_id=${id}&date=${sessionDate.format("YYYY-MM-DD")}`,
            notificationManager,
            (response) => {
                const rawEventData = response?.data?.events;
                if (rawEventData?.length > 0) {
                    let week = startWeek;
                    const events = rawEventData.map(
                        (event, index: number) => {
                            switch (event.type) {
                                case "bonus_activity":
                                    if (event.activities?.length > 0) {
                                        let bonuses = [];
                                        for (const activity of event.activities) {
                                            activity.instances.forEach(
                                                (instance, instanceIndex) => {
                                                    instance.id = activity.id;
                                                    instance.name = activity.name;
                                                    instance.type = activity.type;
                                                    bonuses.push(<SessionBonusPanel key={`session-bonus-panel-${index}-${instanceIndex}`} activity={instance}/>);
                                                }
                                            );
                                        }
                                        return bonuses;
                                    }
                                    return null;
                                case "coaching_plan":
                                    week = event.plan_week;
                                    return <SessionWeekStart key={`session-week-start-${index}`} sessionData={event}/>;
                                case "personal_routine":
                                    return <SessionRoutinePanel key={`session-routine-panel-${index}`} sessionData={event} date={sessionDate} week={week}/>;
                                case "session_badge":
                                    return <SessionBadgeRow key={`session-badge-${index}`} sessionData={event}/>;
                            }
                        }
                    );

                    setSessionEvents(events);
                }

                setStartWeek(response.data.starting_plan_week);
                setSessionData(response.data);
            },
            (error) => {
                notificationManager.addError(error);
            },
            () => {
                setIsLoading(false);
            }
        )
    };

    // ---=== useEffects ===--- //

    useEffect(
        () => {
            void getSessionData(patientId);
        }, []
    );

    useEffect(
        () => {
            if (sessionData !== null) {
                const head = {
                    trail: breadcrumbLinks(),
                    body: {
                        subheader: "VET™ Session Summary",
                        patientName: sessionData ? sessionData.patient : "Patient",
                        buttons: [
                            {
                                title: "Back",
                                click: () => goToPatientActivity(),
                                img: "/icons/flip-backward.svg"
                            }
                        ]
                    }
                };
                setHeaderInfo(head);
            }
        }, [sessionData]
    );

    // ---=== Render ===--- //

    const badgeWarning = () => {
        return (
            <div className="px-5 pb-5 justify-start items-start gap-3 inline-flex">
                <img className="w-5 h-5 relative flex-col justify-start items-start flex" src="/icons/warning_icon.svg" alt="warning icon"/>
                <div className="text-amber-700 text-sm font-normal pt-0.5	leading-tight">
                    Sessions prior to 10th June 2024 cannot display badges in the timeline view.
                </div>
            </div>
        );
    };

    const breadcrumbLinks = () => {
        return [
            { label: "Patients", url: "/" },
            { label: sessionData ? capitalizeWords(sessionData.patient) : "Patient", url: `/patient/${patientId}/usage` },
            { label: sessionDate.format("MMMM DD YYYY"), url: window.location.pathname }
        ];
    };

    const getPageHeaderConfig = () => {
        return headerInfo ? headerInfo : { trail: breadcrumbLinks() };
    };

    return (
        <Page
            componentName={componentName}
            headerConfig={getPageHeaderConfig()}
        >
            {
                isLoading
                    ? (
                        renderLoader("Loading Sessions")
                    )
                    : (
                        <div id="body" className="w-full h-full flex-col inline-flex justify-start whitespace-nowrap overflow-x-hidden overflow-y-auto">
                            <SessionHeader sessionData={sessionData}/>
                            <SessionDivider label={`${sessionDate.format("MMMM DD, YYYY")} (Week ${startWeek})`}/>
                            {
                                sessionDate.isBefore(moment("2024-6-10", "YYYY-MM-DD")) && (
                                    badgeWarning()
                                )
                            }
                            <div className="overscroll-x-none px-5 gap-y-5 justify-center items-center inline-flex flex-col">
                                {sessionEvents}
                            </div>
                        </div>
                    )
            }
        </Page>
    );
};

export default Sessions;
